import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import ICDCodeLookup from './ICDCodeLookup';
import Input from '../ui/Input';
import Select from '../ui/Select';

const DiagnosisCodePanel = ({ diagnoses = [], onAdd, onRemove, onUpdate }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newDiagnosis, setNewDiagnosis] = useState({
    icdCode: null,
    status: 'Active',
    notes: '',
    diagnosedDate: new Date()?.toISOString()?.split('T')?.[0]
  });

  const statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Resolved', label: 'Resolved' },
    { value: 'Chronic', label: 'Chronic' },
    { value: 'Inactive', label: 'Inactive' }
  ];

  const handleAddDiagnosis = () => {
    if (newDiagnosis?.icdCode) {
      onAdd?.({
        id: Date?.now(),
        code: newDiagnosis?.icdCode?.code,
        description: newDiagnosis?.icdCode?.description,
        category: newDiagnosis?.icdCode?.category,
        status: newDiagnosis?.status,
        notes: newDiagnosis?.notes,
        diagnosedDate: newDiagnosis?.diagnosedDate,
        addedDate: new Date()?.toISOString()
      });
      setNewDiagnosis({
        icdCode: null,
        status: 'Active',
        notes: '',
        diagnosedDate: new Date()?.toISOString()?.split('T')?.[0]
      });
      setIsAdding(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-success/10 text-success border-success';
      case 'Resolved':
        return 'bg-muted text-muted-foreground border-border';
      case 'Chronic':
        return 'bg-warning/10 text-warning border-warning';
      case 'Inactive':
        return 'bg-accent/10 text-accent border-accent';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">Diagnosis Codes</h2>
            <p className="text-sm text-muted-foreground mt-1">Manage patient diagnoses with ICD-10 codes</p>
          </div>
          {!isAdding && (
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setIsAdding(true)}
            >
              Add Diagnosis
            </Button>
          )}
        </div>
      </div>

      {isAdding && (
        <div className="p-4 md:p-6 border-b border-border bg-muted/30">
          <h3 className="text-base font-semibold text-foreground mb-4">Add New Diagnosis</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                ICD-10 Code <span className="text-destructive">*</span>
              </label>
              <ICDCodeLookup
                onSelect={(icd) => setNewDiagnosis({ ...newDiagnosis, icdCode: icd })}
                selectedCodes={newDiagnosis?.icdCode}
              />
              {newDiagnosis?.icdCode && (
                <div className="mt-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {newDiagnosis?.icdCode?.code} - {newDiagnosis?.icdCode?.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Category: {newDiagnosis?.icdCode?.category}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Status"
                options={statusOptions}
                value={newDiagnosis?.status}
                onChange={(value) => setNewDiagnosis({ ...newDiagnosis, status: value })}
              />
              <Input
                type="date"
                label="Diagnosed Date"
                value={newDiagnosis?.diagnosedDate}
                onChange={(e) => setNewDiagnosis({ ...newDiagnosis, diagnosedDate: e?.target?.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Clinical Notes</label>
              <textarea
                value={newDiagnosis?.notes}
                onChange={(e) => setNewDiagnosis({ ...newDiagnosis, notes: e?.target?.value })}
                placeholder="Add clinical notes, treatment plan, or additional context..."
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleAddDiagnosis}
                disabled={!newDiagnosis?.icdCode}
              >
                Save Diagnosis
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsAdding(false);
                  setNewDiagnosis({
                    icdCode: null,
                    status: 'Active',
                    notes: '',
                    diagnosedDate: new Date()?.toISOString()?.split('T')?.[0]
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
        {diagnoses?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
              <Icon name="FileText" size={32} className="text-muted-foreground" />
            </div>
            <p className="text-base font-medium text-foreground mb-2">No Diagnoses</p>
            <p className="text-sm text-muted-foreground text-center">Add patient diagnoses with ICD-10 codes</p>
          </div>
        ) : (
          diagnoses?.map((diagnosis) => (
            <div key={diagnosis?.id} className="p-4 md:p-6 hover:bg-muted/30 transition-smooth">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0">
                      <Icon name="Stethoscope" size={20} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-semibold text-foreground">{diagnosis?.code}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(diagnosis?.status)}`}>
                          {diagnosis?.status}
                        </span>
                      </div>
                      <p className="text-sm text-foreground font-medium mb-1">{diagnosis?.description}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="Tag" size={12} />
                          {diagnosis?.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="Calendar" size={12} />
                          Diagnosed: {new Date(diagnosis?.diagnosedDate)?.toLocaleDateString()}
                        </span>
                      </div>
                      {diagnosis?.notes && (
                        <p className="text-sm text-muted-foreground mt-2 p-2 bg-muted/50 rounded">
                          {diagnosis?.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemove?.(diagnosis?.id)}
                  className="flex-shrink-0"
                >
                  <Icon name="Trash2" size={16} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DiagnosisCodePanel;